import axiosInstance from "./api";
import cache from "../utils/cacheUtil";

const ROLES_CACHE_KEY = "user_roles";
const EMULATED_ROLE_CACHE_KEY = "emulated_role";
const IS_EMULATING_CACHE_KEY = "is_emulating";
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const roleHierarchy = [
  "viewer",
  "demonstrator",
  "clinical-author",
  "prompt-engineer",
  "admin",
];

export const roleDescriptions = {
  admin: {
    description: "Has rights to all operations within the assistant.",
    permissions: "Full access to all system functions and settings.",
  },
  "prompt-engineer": {
    description: "Has rights to modify prompts and related technical assets.",
    permissions:
      "Can create, edit, and manage prompts and technical configurations.",
  },
  "clinical-author": {
    description:
      "Has rights to modify prompts and related non-technical assets.",
    permissions:
      "Can create and edit clinical content and non-technical prompts.",
  },
  demonstrator: {
    description:
      "Has rights to demonstrate the system, including creating demo profiles.",
    permissions:
      "Can access demonstration features and create temporary profiles for showcasing purposes.",
  },
  viewer: {
    description: "Has read-only rights to view content within the system.",
    permissions:
      "Can view but not modify any content or settings in the system.",
  },
};

export const currentUserHasRole = async (role) => {
  if (!roleHierarchy.includes(role)) {
    return false;
  }
  const myHighestRole = checkIfEmulating()
    ? getEmulatedRole()
    : getHighestRole(await getMyRoles());
  return roleHierarchy.indexOf(myHighestRole) >= roleHierarchy.indexOf(role);
};

export const getMyRoles = async () => {
  if (checkIfEmulating()) {
    return [getEmulatedRole()];
  }

  let roles = cache.get(ROLES_CACHE_KEY);
  if (roles) {
    return roles;
  }

  try {
    const response = await axiosInstance.get("/api/user/my/roles");
    roles = response.data?.data || [];
    cache.set(ROLES_CACHE_KEY, roles, {
      mode: "memory",
      absoluteTtl: CACHE_DURATION,
      slidingTtl: CACHE_DURATION / 2,
    });
    return roles;
  } catch (error) {
    console.error("Error fetching roles:", error);
    return [];
  }
};

export const setEmulatedRole = (role, currentRoles) => {
  const highestRole = getHighestRole(currentRoles);
  if (canEmulateRole(highestRole, role)) {
    const emulatedRole = role.toLowerCase();
    cache.set(EMULATED_ROLE_CACHE_KEY, emulatedRole, { mode: "local" });
    cache.set(IS_EMULATING_CACHE_KEY, true, { mode: "local" });
    cache.remove(ROLES_CACHE_KEY); // Clear roles cache

    window.location.reload();
    return true;
  }
  return false;
};

export const clearEmulatedRole = () => {
  cache.remove(EMULATED_ROLE_CACHE_KEY);
  cache.remove(IS_EMULATING_CACHE_KEY);
  cache.remove(ROLES_CACHE_KEY); // Clear roles cache

  window.location.reload();
};

export const getEmulatedRole = () => cache.get(EMULATED_ROLE_CACHE_KEY);

export const checkIfEmulating = () =>
  cache.get(IS_EMULATING_CACHE_KEY) || false;

export const initializeEmulationState = () => {
  const storedEmulatedRole = cache.get(EMULATED_ROLE_CACHE_KEY);
  const storedIsEmulating = cache.get(IS_EMULATING_CACHE_KEY);
  if (storedIsEmulating && storedEmulatedRole) {
    cache.set(EMULATED_ROLE_CACHE_KEY, storedEmulatedRole, { mode: "local" });
    cache.set(IS_EMULATING_CACHE_KEY, true, { mode: "local" });
  } else {
    cache.remove(EMULATED_ROLE_CACHE_KEY);
    cache.remove(IS_EMULATING_CACHE_KEY);
  }
};

const getHighestRole = (roles) => {
  return roles.reduce((highest, role) => {
    return roleHierarchy.indexOf(role) > roleHierarchy.indexOf(highest)
      ? role
      : highest;
  });
};

const canEmulateRole = (currentRole, targetRole) => {
  return roleHierarchy.indexOf(currentRole) > roleHierarchy.indexOf(targetRole);
};

export const getEmulateableRoles = (currentRoles) => {
  const highestRole = getHighestRole(currentRoles);
  return roleHierarchy.filter((role) => canEmulateRole(highestRole, role));
};

// This endpoint should be public anyway since OIDC client ID is not sensitive
export const getOidcClientId = async () => {
  try {
    // Use a direct axios call without authentication to avoid circular dependency
    const axios = (await import("axios")).default;
    const { API_URL } = await import("./config");

    const response = await axios.get(`${API_URL}api/oidc-client-id`);
    return response.data?.data || null;
  } catch (error) {
    console.error("Error fetching OIDC client ID:", error);
    return null;
  }
};
